// src/components/Patient/PatientVideoCall.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  joinCall,
  endCall,
  sendAnswer,
  sendIceCandidate,
  fetchMessages,
} from "../../Redux/Slice/Doctor/callSlice";
import { Button, Spin, Layout, Typography } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

const PatientVideoCall = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const roomCode1 = searchParams.get("roomCode");
  const idFromQuery = searchParams.get("id");
  const { roomCode: roomCode2, userId: userIdFromState } = location.state || {};
  const userId =
    userIdFromState ||
    idFromQuery ||
    JSON.parse(localStorage.getItem("user"))?.userId;

  const roomCode = roomCode1 || roomCode2 || "";
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const pendingCandidates = useRef([]);
  const localStreamRef = useRef(null);

  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);

  const [callDuration, setCallDuration] = useState("00:00");

  useEffect(() => {
    if (!joined) return;

    let seconds = 0;
    const interval = setInterval(() => {
      seconds++;
      const m = String(Math.floor(seconds / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      setCallDuration(`${m}:${s}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [joined]);

  const createPeerConnection = useCallback(
    (iceServersList) => {
      const pc = new RTCPeerConnection({ iceServers: iceServersList });

      pc.ontrack = (event) => {
        if (remoteVideoRef.current && event.streams[0]) {
          remoteVideoRef.current.srcObject = event.streams[0];
          remoteVideoRef.current.play().catch(() => {});
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && roomCode) {
          dispatch(
            sendIceCandidate({
              roomCode,
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
              senderId: userId,
            })
          );
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log("ICE state:", pc.iceConnectionState);
      };

      return pc;
    },
    [dispatch, roomCode, userId]
  );

  // Join Call
  useEffect(() => {
    if (!roomCode || !userId) {
      toast.error("Invalid call details");
      navigate("/patient-appointment");
      return;
    }

    const startCall = async () => {
      try {
        const joinRes = await dispatch(
          joinCall({
            roomCode,
            ...(idFromQuery ? { patientId: idFromQuery } : {}),
          })
        ).unwrap();

        const iceServersFromServer = joinRes.iceServers || [
          { urls: "stun:stun.l.google.com:19302" },
        ];

        // Create Peer Connection
        pcRef.current = createPeerConnection();

        // Get local media
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        localStreamRef.current = stream;

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          await localVideoRef.current
            .play()
            .catch((err) => console.warn("play() failed", err));
        }

        // Add tracks to peer connection
        stream.getTracks().forEach((track) => {
          pcRef.current?.addTrack(track, stream);
        });

        setJoined(true);
        toast.success("Joined video call");
      } catch (err) {
        console.error("Join call failed:", err);
        toast.error("Failed to join call");
        navigate("/patient-appointment");
      } finally {
        setLoading(false);
      }
    };

    startCall();
    return () => handleCleanup();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (localStreamRef.current && localVideoRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
      localVideoRef.current
        .play()
        .catch((err) => console.warn("Local video play failed:", err));
    }
  }, [localStreamRef.current]);

  // Poll messages (wait for offer, then answer)
  useEffect(() => {
    if (!joined || !roomCode) return;

    const interval = setInterval(async () => {
      try {
        const msgs = await dispatch(fetchMessages(roomCode)).unwrap();

        if (
          msgs?.offer &&
          pcRef.current &&
          !pcRef.current.currentRemoteDescription
        ) {
          await pcRef.current.setRemoteDescription(
            new RTCSessionDescription(msgs.offer)
          );
          const answer = await pcRef.current.createAnswer();
          await pcRef.current.setLocalDescription(answer);
          dispatch(sendAnswer({ roomCode, sdp: answer.sdp, senderId: userId }));

          // apply queued ICE
          for (const c of pendingCandidates.current) {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
          }
          pendingCandidates.current = [];
        }

        if (Array.isArray(msgs?.iceCandidates)) {
          for (const c of msgs.iceCandidates) {
            if (pcRef.current?.currentRemoteDescription) {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
            } else {
              pendingCandidates.current.push(c);
            }
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [joined, roomCode, userId, dispatch]);

  const handleCleanup = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (pcRef.current) pcRef.current.close();
    pcRef.current = null;
  };

  const handleEndCall = async () => {
    try {
      if (roomCode) await dispatch(endCall({ roomCode })).unwrap();
    } finally {
      handleCleanup();
      navigate("/patient-appointment");
    }
  };

  const toggleVideo = () => {
    const track = localStreamRef.current
      ?.getTracks()
      .find((t) => t.kind === "video");
    if (track) {
      track.enabled = !track.enabled;
      setIsVideoOn(track.enabled);
    }
  };

  const toggleAudio = () => {
    const track = localStreamRef.current
      ?.getTracks()
      .find((t) => t.kind === "audio");
    if (track) {
      track.enabled = !track.enabled;
      setIsAudioOn(track.enabled);
    }
  };

  return (
    <Layout style={{ height: "100vh" }}>
      <div
        style={{
          padding: "15px 30px",
          background: "#002766",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Left Side - Call Info */}
        <div>
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            Patient Video Call
          </Title>
          <div style={{ color: "#d9d9d9", fontSize: "14px" }}>
            Room: {roomCode} |
          </div>
          <div style={{ marginTop: "5px", fontSize: "13px", color: "#52c41a" }}>
            🔒 This call is secure. Screenshots are restricted.
          </div>
        </div>

        {/* Right Side - Call Duration */}
        <div>
          <span
            style={{ fontSize: "16px", fontWeight: "bold", color: "#52c41a" }}
          >
            ⏱ {callDuration || "00:00"}
          </span>
        </div>
      </div>

      <Content
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          background: "#000",
          flex: 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Spin fullscreen />
        ) : (
          <>
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              style={{
                width: "90%",
                height: "100vh",
                background: "black",
                borderRadius: "10px",
                // objectFit: "cover",
              }}
            />
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                position: "absolute",
                bottom: 20,
                right: 20,
                width: "200px",
                height: "150px",
                background: "black",
                borderRadius: "10px",
                border: "2px solid white",
              }}
            />
          </>
        )}
      </Content>

      <Footer style={{ textAlign: "center", background: "#5d626aff" }}>
        {joined && !loading && (
          <>
            <Button onClick={toggleVideo} style={{ marginRight: 10 }}>
              {isVideoOn ? "Turn Video Off" : "Turn Video On"}
            </Button>
            <Button onClick={toggleAudio} style={{ marginRight: 10 }}>
              {isAudioOn ? "Mute" : "Unmute"}
            </Button>
            <Button danger onClick={handleEndCall}>
              End Call
            </Button>
          </>
        )}
      </Footer>
    </Layout>
  );
};

export default PatientVideoCall;
